import { z } from 'zod';

/** Switches the owner can flip in the admin panel. Each one is enforced by the server. */
export const PLATFORM_FLAGS = {
  registrations: 'New accounts can sign up',
  parties: 'Watch Together sessions can be started and joined',
  friendRequests: 'Friend requests and shared titles can be sent',
  chat: 'Friends can send chat messages',
  addonInstalls: 'Addons can be installed',
  webApp: 'The web app (web.teaplus.tv) can be used',
  premium:
    'Tea+ Premium (in testing): plans, limits, the Premium badge, Up Next voting and episode alerts. Off works as before Premium',
} as const;
export type PlatformFlag = keyof typeof PLATFORM_FLAGS;
export type PlatformFlags = Record<PlatformFlag, boolean>;

export const DEFAULT_FLAGS: PlatformFlags = {
  registrations: true,
  parties: true,
  friendRequests: true,
  chat: true,
  addonInstalls: true,
  webApp: true,
  premium: true,
};

export interface MaintenanceState {
  enabled: boolean;
  message: string;
  /** When it's expected to end, if known. Shown to users. */
  until: string | null;
}

export const DEFAULT_MAINTENANCE_MESSAGE =
  'Tea+ is having some maintenance right now. Please try again in a little while.';

export type AnnouncementLevel = 'info' | 'success' | 'warning' | 'critical';
export type AnnouncementAudience = 'all' | 'desktop' | 'web';

export interface Announcement {
  id: string;
  title: string;
  body: string;
  level: AnnouncementLevel;
  dismissible: boolean;
}

/** What the apps fetch on start: GET /api/system/status. */
export interface PlatformStatus {
  maintenance: MaintenanceState;
  announcements: Announcement[];
  /** Set when the signed-in account is restricted: the apps stop working. id is for support. */
  restricted?: { id: string } | null;
  /** Set when the active profile is restricted: only that profile stops working. */
  profileRestricted?: { id: string; name: string } | null;
}

export const PROFILE_RESTRICTED_MESSAGE =
  'This profile has been restricted, so it can no longer use Tea+. Please contact Tea+ Support for more information.';

export const ACCOUNT_RESTRICTED_MESSAGE =
  'Your account has been restricted, so you can no longer use Tea+. Please contact Tea+ Support for more information.';

export const TELEMETRY_KINDS = [
  'pause',
  'resume',
  'buffer',
  'player_error',
  'client_error',
  'link_copied',
  /** What the player had to do to keep a video going, and how long starting took. */
  'playback_diag',
] as const;
export type ClientTelemetryKind = (typeof TELEMETRY_KINDS)[number];

export const clientTelemetrySchema = z.object({
  events: z
    .array(
      z.object({
        kind: z.enum(TELEMETRY_KINDS),
        mediaId: z.string().max(64).optional(),
        /** Seconds spent buffering (buffer), or where in the video it happened. */
        seconds: z.number().min(0).max(86_400).optional(),
        position: z.number().min(0).max(1e6).optional(),
        message: z.string().max(500).optional(),
        stack: z.string().max(2000).optional(),
        where: z.string().max(120).optional(),
      }),
    )
    .min(1)
    .max(25),
});
export type ClientTelemetry = z.infer<typeof clientTelemetrySchema>;
